import React, { ReactNode, useMemo, useState, ReactElement, useRef, useCallback } from 'react';
import { motion, Variant, useAnimation } from 'framer-motion';
import classNames from 'classnames';

import { gtmHelpers } from '@digital-dealer/utils';
import { GTMEventAction, GTMEventCategory } from '@digital-dealer/enums';

import { IModalContext, defaultValue, Context, IModalControls } from './context';
import styles from './styles.module.scss';

export interface IModalProviderProps {
  children: ReactNode;
}

const opened: Variant = {
  height: 'auto',
  opacity: 1,
};

const collapsed: Variant = {
  height: 0,
  opacity: 0,
  transition: {
    duration: 0.5,
  },
};

const ModalProvider = ({ children }: IModalProviderProps): ReactElement => {
  const [portalNode, setPortalNode] = useState<IModalContext['portalNode']>(defaultValue.portalNode);
  const [isCloseAvailable, setIsCloseAvailable] = useState<IModalContext['isCloseAvailable']>(defaultValue.isCloseAvailable);

  const currentModal = useRef<string>('');

  const modalControls = useRef<Record<string, IModalControls | undefined>>({});
  const previousOffsets = useRef<Record<string, number>>({});

  const modalsStack = useRef<string[]>([]);

  const [isScrollSynchronized, setIsScrollSynchronized] = useState<boolean>(false);

  const animationControls = useAnimation();

  const register = useCallback(
    (name: string, controls: IModalControls): void => {
      modalControls.current[name] = controls;
    },
    [],
  );

  const unregister = useCallback(
    (name: string): void => {
      modalControls.current[name] = undefined;
      previousOffsets.current[name] = 0;

      modalsStack.current = modalsStack.current.filter((modal: string): boolean => modal !== name);

      if (currentModal.current === name) {
        currentModal.current = '';
        setIsScrollSynchronized(false);

        void animationControls.start(opened);
      }
    },
    [animationControls],
  );

  const open = useCallback(
    async (name: string): Promise<void> => {
      setIsCloseAvailable(false);

      previousOffsets.current[name] = await window.xprops.getScrollTop();

      setIsScrollSynchronized(true);

      await window.xprops.scrollToWidget();

      await Promise.all([
        currentModal.current ? modalControls.current[currentModal.current]?.collapse() : animationControls.start(collapsed),
        modalControls.current[name]?.open(),
      ]);

      if (currentModal.current) {
        modalsStack.current.push(currentModal.current);
      }

      currentModal.current = name;

      setIsCloseAvailable(true);
    },
    [animationControls],
  );

  const close = useCallback(
    async (name: string): Promise<void> => {
      setIsScrollSynchronized(false);
      setIsCloseAvailable(false);

      const previous = modalsStack.current.pop();

      await window.xprops.scrollToWidget();

      if (previous) {
        await Promise.all([
          modalControls.current[previous]?.open(),
          modalControls.current[name]?.collapse(),
        ]);

        currentModal.current = previous;
      } else {
        await Promise.all([
          modalControls.current[name]?.collapse(),
          animationControls.start(opened),
        ]);

        currentModal.current = '';
      }

      const currentWidgetOffset = await window.xprops.getScrollTop();
      await window.xprops.scrollTo({ top: previousOffsets.current[name] - currentWidgetOffset });

      previousOffsets.current[name] = 0;

      gtmHelpers.emitEvent(GTMEventCategory.Click, GTMEventAction.Close, name);

      setIsCloseAvailable(true);
    },
    [animationControls],
  );

  const value = useMemo(
    (): IModalContext => ({
      portalNode,
      register,
      unregister,
      open,
      close,
      isCloseAvailable,
    }),
    [portalNode, register, unregister, open, close, isCloseAvailable],
  );

  return (
    <Context.Provider value={value}>
      <motion.div
        className={
          classNames(
            styles.contentWrapper,
            {
              [styles.isCollapsed]: isScrollSynchronized,
            },
          )
        }
        initial={false}
        animate={animationControls}
      >
        {children}
      </motion.div>

      <div
        className={styles.modalWrapper}
        ref={setPortalNode}
      />
    </Context.Provider>
  );
};

export default ModalProvider;
